import React, { memo, useCallback, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Bolt as EventsIcon,
  Rule as RulesIcon,
  Dashboard as TemplatesIcon,
  Assignment as ActionLogIcon,
  TableChart as DatasetsIcon,
} from '@mui/icons-material';
import ModalHeader from '../../../shared/components/ModalHeader';
import ModalFooter from '../../../shared/components/ModalFooter';
import DeleteConfirmDialog from '../../../shared/components/DeleteConfirmDialog';
import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import RulesTabContent from './RulesTabContent';
import TemplatesTabContent from './TemplatesTabContent';
import EventsView from './EventsView';
import ActionLogTabContent from './ActionLogTabContent';
import DatasetsTabContent from './datasets/DatasetsTabContent';
import type { RowEventTarget } from '../../../types/frontend';
import { useEventManagement } from '../hooks/useEventManagement';
import { useFeatureSwitch } from '../../../shared/hooks/useFeatureSwitch';
import { useViewport } from '../../../shared/hooks/useViewport';
import { useDatasetWriteAccess } from '../hooks/useEventDatasets';
import { datasetSeedFromEvent, type DatasetSeed } from '../utils/datasetFormUtils';
import type { LiveEvent } from '../../../types/frontend';

const EXPLORER_TAB = 2;
const DATASETS_TAB = 4;

interface EventManagementModalProps {
  customerId: string;
  isOpen: boolean;
  onClose: () => void;
  initialOpsItemId?: string | null;
  onInvestigationCreated?: (investigation: import('../../../types').Investigation, initialMessage?: string) => void;
}

const EventManagementModal: React.FC<EventManagementModalProps> = ({
  customerId,
  isOpen,
  onClose,
  initialOpsItemId,
  onInvestigationCreated,
}) => {
  const {
    customer,
    rules,
    templates,
    activeTab,
    setActiveTab,
    loading,
    error,
    ruleMode,
    ruleEditorKey,
    loadingRule,
    selectedTemplate,
    selectedTemplateItem,
    isNewTemplate,
    loadingTemplate,
    deleteTarget,
    footerCount,
    footerLabel,
    snackbar,
    closeSnackbar,
    setEventCount,
    setActionLogCount,
    setError,
    setDeleteTarget,
    handleSelectRule,
    handleNewRule,
    handleRuleSaved,
    handleDeleteRuleConfirm,
    executeDeleteRule,
    handleDiscardRule,
    handleSelectTemplate,
    handleNewTemplate,
    handleTemplateSaved,
    handleDeleteTemplateConfirm,
    executeDeleteTemplate,
    handleDiscardTemplate,
    handleUseTemplateAsRule,
    handleCreateFromEvent,
    handleStartInvestigation,
    handleStartInvestigationFromActionLog,
  } = useEventManagement({ customerId, isOpen, onClose, onInvestigationCreated, initialOpsItemId });

  // The Datasets tab is the Event Datasets entry point: the switch is read here and nowhere below it.
  const datasetsEnabled = useFeatureSwitch('event_datasets');
  const { isCompact } = useViewport();
  const canEditDatasets = useDatasetWriteAccess();
  const [datasetCount, setDatasetCount] = useState(0);
  const [datasetSeed, setDatasetSeed] = useState<DatasetSeed | null>(null);
  const onDatasetsTab = datasetsEnabled && activeTab === DATASETS_TAB;
  const takeSeed = useCallback(() => setDatasetSeed(null), []);
  const handleCreateDatasetFromEvent = (event: LiveEvent) => {
    setDatasetSeed(datasetSeedFromEvent(event));
    setActiveTab(DATASETS_TAB);
  };
  // A dataset row's event opens in the Explorer, the reverse of starting a dataset from an Explorer event.
  const [eventTarget, setEventTarget] = useState<RowEventTarget | null>(null);
  const takeEventTarget = useCallback(() => setEventTarget(null), []);
  const handleOpenEvent = (target: RowEventTarget) => {
    setEventTarget(target);
    setActiveTab(EXPLORER_TAB);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      fullScreen={isCompact}
      PaperProps={{ sx: isCompact ? undefined : { width: '95vw', height: '95vh', maxWidth: '95vw', maxHeight: '95vh' } }}
    >
      <ModalHeader
        icon={RulesIcon}
        title="Event Management"
        onClose={onClose}
        sx={isCompact ? { px: 2 } : undefined}
        titleExtra={customer ? <Chip label={customer.name} title={customer.name} size="small" variant="outlined" sx={{ maxWidth: { xs: '30vw', sm: 'none' } }} /> : undefined}
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: isCompact ? 0 : 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          <Tab label="Rules" icon={<RulesIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
          <Tab label="Templates" icon={<TemplatesIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
          <Tab label="Event Explorer" icon={<EventsIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
          <Tab label="Action Log" icon={<ActionLogIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
          {datasetsEnabled && <Tab label="Datasets" icon={<DatasetsIcon />} iconPosition="start" sx={{ minHeight: 48 }} />}
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 0, display: 'flex', height: '100%', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {activeTab === 0 && (
              <RulesTabContent
                customerId={customerId}
                rules={rules}
                ruleMode={ruleMode}
                ruleEditorKey={ruleEditorKey}
                loadingRule={loadingRule}
                error={error}
                onSelectRule={handleSelectRule}
                onNewRule={handleNewRule}
                onRuleSaved={handleRuleSaved}
                onDeleteRule={ruleMode.kind === 'editing' ? handleDeleteRuleConfirm : undefined}
                onDiscardRule={handleDiscardRule}
                onClearError={() => setError(null)}
              />
            )}

            {activeTab === 1 && customerId && (
              <TemplatesTabContent
                customerId={customerId}
                customerName={customer?.name}
                templates={templates}
                selectedTemplate={selectedTemplate}
                selectedTemplateItem={selectedTemplateItem}
                isNewTemplate={isNewTemplate}
                loadingTemplate={loadingTemplate}
                onSelectTemplate={handleSelectTemplate}
                onNewTemplate={handleNewTemplate}
                onTemplateSaved={handleTemplateSaved}
                onDeleteTemplate={handleDeleteTemplateConfirm}
                onDiscardTemplate={handleDiscardTemplate}
                onUseAsRule={handleUseTemplateAsRule}
              />
            )}

            {activeTab === EXPLORER_TAB && customerId && (
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <EventsView
                  customerId={customerId}
                  onCreateRule={handleCreateFromEvent}
                  onCreateDataset={datasetsEnabled && canEditDatasets ? handleCreateDatasetFromEvent : undefined}
                  onStartInvestigation={handleStartInvestigation}
                  onEventCountChange={setEventCount}
                  eventTarget={eventTarget}
                  onTargetTaken={takeEventTarget}
                />
              </Box>
            )}

            {activeTab === 3 && customerId && (
              <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                <ActionLogTabContent
                  customerId={customerId}
                  onCountChange={setActionLogCount}
                  onStartInvestigation={handleStartInvestigationFromActionLog}
                  initialOpsItemId={initialOpsItemId}
                />
              </Box>
            )}

            {onDatasetsTab && customer && (
              <DatasetsTabContent
                customerId={customerId}
                customerName={customer.name}
                onCountChange={setDatasetCount}
                seed={datasetSeed}
                onSeedTaken={takeSeed}
                onOpenEvent={handleOpenEvent}
              />
            )}
          </>
        )}
      </DialogContent>

      <ModalFooter
        count={onDatasetsTab ? datasetCount : footerCount}
        label={onDatasetsTab ? (datasetCount === 1 ? 'dataset' : 'datasets') : footerLabel}
        onClose={onClose}
      />

      <DeleteConfirmDialog open={deleteTarget?.type === 'rule'} title="Delete Rule" onCancel={() => setDeleteTarget(null)} onConfirm={executeDeleteRule}>
        <Typography>Are you sure you want to delete this rule? This action cannot be undone.</Typography>
      </DeleteConfirmDialog>

      <DeleteConfirmDialog open={deleteTarget?.type === 'template'} title="Delete Template" onCancel={() => setDeleteTarget(null)} onConfirm={executeDeleteTemplate}>
        <Typography>Are you sure you want to delete this template?</Typography>
      </DeleteConfirmDialog>

      <SnackbarAlert snackbar={snackbar} onClose={closeSnackbar} />
    </Dialog>
  );
};

export default memo(EventManagementModal);
